import { type Column, EmptyState, Pill, Table } from "@/components/common";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { usePiiAccessLogs } from "@/hooks/useLgpd";
import type { PiiAccessLog } from "@/types/pii-access-log";

// FE-028: painel read-only da trilha de acessos a PII (Art. 37). Escopado ao
// workspace ativo (X-Workspace-Id), gated por lgpd.pii_access_logs.read no backend.
// Mostra quem acessou, qual dado/recurso, a ação e quando.

const ACTION_TONE: Record<string, "ativa" | "onb" | "alerta" | "diag"> = {
  read: "ativa",
  export: "onb",
  decrypt: "diag",
};

function dateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
}

export function PiiAccessLogsPanel() {
  const { activeWorkspaceId } = useActiveWorkspace();
  const { data: logs = [], isLoading } = usePiiAccessLogs(activeWorkspaceId, { limit: 200 });

  const columns: Column<PiiAccessLog>[] = [
    {
      id: "when",
      header: "Data",
      cell: (l) => <span className="td-faded">{dateTime(l.created_at)}</span>,
    },
    {
      id: "user",
      header: "Quem acessou",
      cell: (l) => <span className="td-muted">Usuário #{l.user_id}</span>,
    },
    {
      id: "data_type",
      header: "Tipo de dado",
      cell: (l) => <span className="razao">{l.data_type}</span>,
    },
    {
      id: "resource",
      header: "Recurso",
      cell: (l) => (
        <span className="td-faded">
          {l.resource_type} #{l.resource_id}
        </span>
      ),
    },
    {
      id: "action",
      header: "Ação",
      cell: (l) => <Pill tone={ACTION_TONE[l.action] ?? "diag"}>{l.action}</Pill>,
    },
    {
      id: "endpoint",
      header: "Endpoint",
      cell: (l) => <span className="td-faded">{l.endpoint ?? "—"}</span>,
    },
  ];

  if (activeWorkspaceId == null) {
    return (
      <EmptyState
        icon="shield-check"
        size="lg"
        title="Selecione um workspace"
        description="A trilha de acessos a PII é escopada ao workspace ativo."
      />
    );
  }

  return (
    <>
      <div className="emp-toolbar">
        <span className="emp-toolbar-counter">{logs.length} acessos</span>
      </div>
      <Table
        rows={logs}
        columns={columns}
        getKey={(l) => String(l.id)}
        empty={
          <EmptyState
            icon="shield-check"
            size="lg"
            title={isLoading ? "Carregando…" : "Nenhum acesso registrado"}
            description={
              isLoading
                ? undefined
                : "Acessos a dados pessoais (Art. 37) aparecerão aqui conforme ocorrerem."
            }
          />
        }
      />
    </>
  );
}
